import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import HelperText from './../HelperText/HelperText';
import { Icon } from 'ui';
import ValidationMessage from './../ValidationMessage/ValidationMessage';
import useInputText from './useInputText';

const InputText = React.forwardRef(
  (
    {
      'aria-label': ariaLabel,
      background,
      className,
      disabled,
      error,
      helperText,
      icon,
      iconAriaLabel,
      iconColor,
      iconLeft,
      iconSize,
      label,
      name,
      onChange,
      onClickIcon,
      onKeyPress,
      placeholder,
      referer,
      required,
      success,
      type,
      variant,
      ...props
    },
    ref
  ) => {
    const {
      refInput, getIconAriaLabelValue, fieldId, hasError,
      isIconClickable, shouldRenderValidation, ariaDescribedBy,
      validationType, validationMessage, validationConfig
    } = useInputText({
      disabled,
      error,
      helperText,
      icon,
      iconAriaLabel,
      name,
      onClickIcon,
      ref,
      referer,
      success,
    });
    const iconElement = icon ? <Icon name={icon} size={iconSize} color={iconColor} /> : null;
    const decorativeIcon = icon ? (
      <div className="input-text__icon" aria-hidden="true">
        {iconElement}
      </div>
    ) : null;

    return (
      <div
        className={clsx(
          'input-text',
          `input-text--${variant}`,
          className,
          `input-text--background-${background}`,
          { 'input-text--icon': !!icon },
          { 'input-text--icon-left': iconLeft },
          { 'input-text--error': hasError },
          { 'input-text--has-validation-icon': shouldRenderValidation }
        )}
      >
        {label && (
          <label id={fieldId.labelId} htmlFor={fieldId.baseId} className="input-text__label">
            {label} {required && <sup aria-hidden="true">*</sup>}
          </label>
        )}
        <div className="input-text__input-container">
          <input
            name={name}
            className={clsx('input-text__input', { 'input-text__input--disabled': disabled })}
            type={type}
            placeholder={placeholder}
            onChange={onChange}
            disabled={disabled}
            ref={refInput}
            onKeyPress={onKeyPress}
            {...props}
            id={fieldId.baseId}
            aria-describedby={ariaDescribedBy}
            required={required}
            aria-required={required ? 'true' : undefined}
            aria-invalid={hasError ? 'true' : undefined}
            aria-label={label ? undefined : ariaLabel}
          />
          {/* Action icon (left/right): clickable renders as button, otherwise decorative div */}
          {icon && isIconClickable ? (
            <button
              type="button"
              className="input-text__icon input-text--icon-click"
              onClick={onClickIcon}
              aria-label={getIconAriaLabelValue}
            >
              {iconElement}
            </button>
          ) : decorativeIcon}
          {/* Validation icon (right): shows check or caution based on error/success */}
          {validationType && validationConfig[validationType] && (
            <div className="input-text__validation-icon" aria-hidden="true">
              <Icon
                name={validationConfig[validationType].inlineIcon}
                size="md"
                color={validationConfig[validationType].color}
              />
            </div>
          )}
        </div>
        {helperText && (
          <HelperText id={fieldId.helperId} text={helperText} />
        )}
        {/* String messages use text prop; JSX (e.g. <ErrorText>) is passed as children for backwards compatibility */}
        {shouldRenderValidation && (
          typeof validationMessage === 'string' ? (
            <ValidationMessage id={fieldId.validationId} type={validationType} text={validationMessage} />
          ) : (
            <ValidationMessage id={fieldId.validationId} type={validationType}>
              {validationMessage}
            </ValidationMessage>
          )
        )}
      </div>
    );
  }
);

InputText.propTypes = {
  'aria-label': PropTypes.string,
  background: PropTypes.oneOf(['default', 'grey']),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  helperText: PropTypes.string,
  icon: PropTypes.string,
  iconAriaLabel: PropTypes.string,
  iconColor: PropTypes.string,
  iconLeft: PropTypes.bool,
  iconSize: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onClickIcon: PropTypes.func,
  onKeyPress: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  referer: PropTypes.object,
  required: PropTypes.bool,
  success: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  type: PropTypes.string,
  value: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'focus']),
};

InputText.defaultProps = {
  background: 'default',
  disabled: false,
  iconLeft: false,
  onChange: () => null,
  onClickIcon: undefined,
  onKeyPress: () => {},
  placeholder: 'Write here',
  type: 'text',
  variant: 'default',
  iconSize: 'lg',
};

InputText.displayName = 'InputText';

export default InputText;
